import React, { createContext, useContext, useReducer, useEffect, useRef, ReactNode } from 'react';
import { api, CartItem, Product } from '../services/api';
import { useUser } from './UserContext';

// Guest cart utility functions
const GUEST_CART_KEY = 'ecofashion_guest_cart';

const saveGuestCart = (items: GuestCartItem[]) => {
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(items));
};

const loadGuestCart = (): GuestCartItem[] => {
  try {
    const saved = localStorage.getItem(GUEST_CART_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
};

const generateGuestItemId = () => {
  return 'guest_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
};

// Guest cart item interface for localStorage
interface GuestCartItem {
  id: string; // Generated ID for guest items
  productId: number;
  quantity: number;
  size?: string;
  product?: Product;
}

interface CartState {
  items: CartItem[];
  loading: boolean;
  error: string | null;
  isOpen: boolean;
  isGuest: boolean; // Track if using guest cart
}

type CartAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_ITEMS'; payload: CartItem[] }
  | { type: 'ADD_ITEM'; payload: CartItem }
  | { type: 'UPDATE_ITEM'; payload: { id: number | string; quantity: number } }
  | { type: 'REMOVE_ITEM'; payload: number | string }
  | { type: 'CLEAR_CART' }
  | { type: 'TOGGLE_CART' }
  | { type: 'SET_CART_OPEN'; payload: boolean }
  | { type: 'SET_GUEST_MODE'; payload: boolean };

const initialState: CartState = {
  items: [],
  loading: false,
  error: null,
  isOpen: false,
  isGuest: false,
};

function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_ITEMS':
      return { ...state, items: action.payload };
    case 'ADD_ITEM':
      // Check if item already exists (vintage items are unique)
      const existingItem = state.items.find(
        item => item.productId === action.payload.productId && item.size === action.payload.size
      );
      if (existingItem) {
        // Don't add duplicate vintage items, just return current state
        return state;
      }
      return { ...state, items: [...state.items, action.payload] };
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(item => {
          const itemId = item.id ? item.id.toString() : '';
          const payloadId = action.payload.id ? action.payload.id.toString() : '';
          return itemId === payloadId
            ? { ...item, quantity: action.payload.quantity }
            : item;
        }),
      };
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter(item => {
          const itemId = item.id ? item.id.toString() : '';
          const payloadId = action.payload ? action.payload.toString() : '';
          return itemId !== payloadId;
        }),
      };
    case 'CLEAR_CART':
      return { ...state, items: [] };
    case 'TOGGLE_CART':
      return { ...state, isOpen: !state.isOpen };
    case 'SET_CART_OPEN':
      return { ...state, isOpen: action.payload };
    case 'SET_GUEST_MODE':
      return { ...state, isGuest: action.payload };
    default:
      return state;
  }
}

interface CartContextType {
  state: CartState;
  addToCart: (productId: number, quantity: number, size?: string) => Promise<void>;
  updateCartItem: (id: number | string, quantity: number) => Promise<void>;
  removeFromCart: (id: number | string) => Promise<void>;
  clearCart: () => Promise<void>;
  loadCart: () => Promise<void>;
  toggleCart: () => void;
  openCart: () => void;
  closeCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

interface CartProviderProps {
  children: ReactNode;
}

export const CartProvider: React.FC<CartProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);

  const loadCart = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      if (api.isAuthenticated()) {
        // Load authenticated user cart
        dispatch({ type: 'SET_GUEST_MODE', payload: false });
        const cartItems = await api.getCart();
        dispatch({ type: 'SET_ITEMS', payload: cartItems });
      } else {
        // Load guest cart from localStorage
        dispatch({ type: 'SET_GUEST_MODE', payload: true });
        const guestCartItems = loadGuestCart();
        
        // Convert guest cart items to CartItem format and fetch product details
        const cartItemsWithProducts: CartItem[] = [];
        for (const guestItem of guestCartItems) {
          try {
            const product = await api.getProduct(guestItem.productId);
            cartItemsWithProducts.push({
              id: guestItem.id as any, // Use string ID for guest items
              userId: 0, // Guest user ID
              productId: guestItem.productId,
              quantity: guestItem.quantity,
              size: guestItem.size,
              product: product
            });
          } catch (error) {
            console.error(`Failed to load product ${guestItem.productId}:`, error);
          }
        }
        
        dispatch({ type: 'SET_ITEMS', payload: cartItemsWithProducts });
      }
    } catch (error: any) {
      console.error('Failed to load cart:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to load cart' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const addToCart = async (productId: number, quantity: number, size?: string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      if (api.isAuthenticated()) {
        // Check if item already exists in current state
        const existingItem = state.items.find(
          item => item.productId === productId && item.size === size
        );
        if (existingItem) {
          // Silently ignore duplicate additions
          return;
        }
        
        // Add to authenticated user cart
        console.log('🛒 Trying to add to authenticated cart:', { productId, quantity, size });
        
        // TRY API FIRST
        let response;
        try {
          response = await api.addToCart({ productId, quantity, size });
          console.log('✅ API addToCart success:', response);
        } catch (apiError) {
          console.log('❌ API addToCart failed, trying direct call...');
          // FALLBACK: Direct backend call
          const directResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/cart`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            },
            body: JSON.stringify({ productId, quantity, size })
          });
          
          if (!directResponse.ok) {
            throw new Error('Add to cart failed');
          }
          
          response = await directResponse.json();
          console.log('✅ Direct addToCart success:', response);
        }
        
        // Fix empty cart item from backend
        let cartItem = response.cartItem;
        if (!cartItem || Object.keys(cartItem).length === 0) {
          // Backend returned empty object, create proper cart item
          const productDetails = await api.getProduct(productId);
          cartItem = {
            id: Date.now(), // Temporary ID since backend doesn't provide one
            userId: 0, // Will be set by backend
            productId: productId,
            quantity: quantity,
            size: size,
            product: productDetails
          };
          console.log('Created cart item from scratch:', cartItem);
        } else if (!cartItem.product) {
          // Cart item exists but missing product details
          const productDetails = await api.getProduct(productId);
          cartItem.product = productDetails;
          console.log('Added product details to existing cart item:', productDetails);
        }
        
        dispatch({ type: 'ADD_ITEM', payload: cartItem });
      } else {
        // Add to guest cart
        const guestCartItems = loadGuestCart();
        const existingItemIndex = guestCartItems.findIndex(
          item => item.productId === productId && item.size === size
        );
        
        let newGuestItem: GuestCartItem;
        
        if (existingItemIndex >= 0) {
          // Item already exists - vintage items are unique, don't add duplicate
          return;
        } else {
          // Create new item
          newGuestItem = {
            id: generateGuestItemId(),
            productId,
            quantity,
            size
          };
          guestCartItems.push(newGuestItem);
        }
        
        // Save to localStorage
        saveGuestCart(guestCartItems);
        
        // Fetch product details and add to state
        const product = await api.getProduct(productId);
        const cartItem: CartItem = {
          id: newGuestItem.id as any,
          userId: 0,
          productId,
          quantity: newGuestItem.quantity,
          size,
          product
        };
        
        dispatch({ type: 'ADD_ITEM', payload: cartItem });
      }
      
      // Auto-open cart when item is added
      dispatch({ type: 'SET_CART_OPEN', payload: true });
    } catch (error: any) {
      console.error('Failed to add to cart:', error);
      let errorMessage = 'Failed to add to cart';
      
      if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Cannot connect to server. Please check your connection and try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateCartItem = async (id: number | string, quantity: number) => {
    // For vintage items, we don't allow quantity updates
    // If quantity is 0 or less, remove the item
    if (quantity <= 0) {
      await removeFromCart(id);
      return;
    }
    
    // For any other quantity change, silently ignore (vintage items are unique)
  };

  const removeFromCart = async (id: number | string) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      if (api.isAuthenticated() && typeof id === 'number') {
        // Remove from authenticated user cart with MOBILE FALLBACK
        console.log('🗑️ Trying to remove from authenticated cart:', id);
        
        // TRY API FIRST
        try {
          await api.removeFromCart(id);
          console.log('✅ API removeFromCart success');
        } catch (apiError) {
          console.log('❌ API removeFromCart failed, trying direct call...');
          // FALLBACK: Direct backend call
          const directResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/cart/${id}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });
          
          if (!directResponse.ok) {
            throw new Error('Remove from cart failed');
          }
          
          console.log('✅ Direct removeFromCart success');
        }
      } else {
        // Remove from guest cart
        const guestCartItems = loadGuestCart();
        const filteredItems = guestCartItems.filter(item => item.id !== id.toString());
        saveGuestCart(filteredItems);
      }
      
      dispatch({ type: 'REMOVE_ITEM', payload: id });
    } catch (error: any) {
      console.error('Failed to remove from cart:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to remove from cart' });
      // Don't throw error - let user continue using the app
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const clearCart = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: null });
      
      if (api.isAuthenticated()) {
        // Clear authenticated user cart
        await api.clearCart();
      } else {
        // Clear guest cart
        localStorage.removeItem(GUEST_CART_KEY);
      }
      
      dispatch({ type: 'CLEAR_CART' });
    } catch (error: any) {
      console.error('Failed to clear cart:', error);
      dispatch({ type: 'SET_ERROR', payload: error.message || 'Failed to clear cart' });
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const toggleCart = () => {
    dispatch({ type: 'TOGGLE_CART' });
  };

  const openCart = () => {
    dispatch({ type: 'SET_CART_OPEN', payload: true });
  };

  const closeCart = () => {
    dispatch({ type: 'SET_CART_OPEN', payload: false });
  };

  const getTotalItems = () => {
    if (!Array.isArray(state.items)) {
      return 0;
    }
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    if (!Array.isArray(state.items)) {
      return 0;
    }
    return state.items.reduce((total, item) => {
      const price = item.product ? Number(item.product.price) : 0;
      return total + (price * item.quantity);
    }, 0);
  };

  // Load cart on mount
  useEffect(() => {
    loadCart();
  }, []);

  // When a guest logs in (or registers) mid-session, merge whatever was in
  // their localStorage cart into their account, then reload from the server.
  const { isAuthenticated } = useUser();
  const wasAuthenticatedRef = useRef(isAuthenticated);

  useEffect(() => {
    const wasAuthenticated = wasAuthenticatedRef.current;
    wasAuthenticatedRef.current = isAuthenticated;

    if (!wasAuthenticated && isAuthenticated) {
      const guestCartItems = loadGuestCart();

      if (guestCartItems.length === 0) {
        loadCart();
        return;
      }

      (async () => {
        for (const item of guestCartItems) {
          try {
            await api.addToCart({ productId: item.productId, quantity: item.quantity, size: item.size });
          } catch (error) {
            // Item may already be in the account cart (unique constraint) — ignore and continue.
            console.error('Failed to merge guest cart item:', item, error);
          }
        }
        localStorage.removeItem(GUEST_CART_KEY);
        await loadCart();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const contextValue: CartContextType = {
    state,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    loadCart,
    toggleCart,
    openCart,
    closeCart,
    getTotalItems,
    getTotalPrice,
  };

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};